import {
  Button,
  Checkbox,
  DataTable,
  DataTableColumnHeader,
  DataTableFacetedFilter,
  DataTableSkeleton,
  DebouncedInput,
  useDataTable,
} from '@data-delivery/d3s';
import {
  assignmentsQueries,
  sftpUsersKeys,
  useBulkAssignAssignmentsToUserMutation,
  useBusinessGroupsRQ,
} from '@data-delivery/permission-ui-data-access-state';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { showErrorToast } from '../../utils/error-utils';
import { ComposableSheet } from '../sheets/composable-sheet';

interface AssignUserAssignmentsSheetProps {
  userId: string;
  open: boolean;
  onClose: () => void;
  businessGroupId?: string;
}

export function AssignUserAssignmentsSheet({
  userId,
  open,
  onClose,
  businessGroupId,
}: AssignUserAssignmentsSheetProps) {
  const { data: assignments = [], isLoading, isError, error } = useQuery(assignmentsQueries.list());
  const { data: userAssignments = [], isLoading: isLoadingUserAssignments } = useQuery(
    sftpUsersKeys.assignmentsQuery(userId),
  );
  const { data: businessGroups, isLoading: isLoadingBusinessGroups } = useBusinessGroupsRQ();
  const bulkAssign = useBulkAssignAssignmentsToUserMutation();
  const [globalSearch, setGlobalSearch] = useState('');

  const [selectedIds, setSelectedIds] = useState<string[]>(() =>
    open ? userAssignments.map(a => a.id) : [],
  );
  const [initialAssignmentIds] = useState<string[]>(() =>
    open ? userAssignments.map(a => a.id) : [],
  );

  const bgNamesMap = useMemo(() => {
    const map = new Map<string, string>();
    businessGroups?.forEach(bg => {
      map.set(bg.id, bg.name || bg.shortName || bg.id);
    });
    return map;
  }, [businessGroups]);

  const scopedAssignments = useMemo(
    () =>
      businessGroupId ? assignments.filter(a => a.businessGroup === businessGroupId) : assignments,
    [assignments, businessGroupId],
  );

  const uniqueBusinessGroupIds = useMemo(
    () =>
      businessGroupId
        ? [businessGroupId]
        : Array.from(new Set(assignments.map(a => a.businessGroup)))
            .filter(Boolean)
            .sort(),
    [assignments, businessGroupId],
  );

  type Row = (typeof assignments)[number];

  const columns = useMemo<Array<ColumnDef<Row>>>(
    () => [
      {
        id: 'select',
        header: ({ column }) => <DataTableColumnHeader column={column} title='' />,
        cell({ row }) {
          const checked = selectedIds.includes(row.original.id);
          return (
            <Checkbox
              checked={checked}
              aria-label={`Select ${row.original.name}`}
              onCheckedChange={() => {
                setSelectedIds(prev =>
                  prev.includes(row.original.id)
                    ? prev.filter(p => p !== row.original.id)
                    : [...prev, row.original.id],
                );
              }}
            />
          );
        },
        enableSorting: false,
        size: 32,
      },
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Name' />,
        cell: ({ getValue }) => <span className='truncate'>{getValue<string>()}</span>,
      },
      {
        accessorKey: 'businessGroup',
        header: ({ column }) => <DataTableColumnHeader column={column} title='Business Group' />,
        cell({ getValue }) {
          const bgId = getValue<string | undefined>();
          return <span>{bgId ? bgNamesMap.get(bgId) || bgId : '\u2014'}</span>;
        },
        filterFn(row, columnId, filterValues) {
          if (!filterValues || filterValues.length === 0) {
            return true;
          }

          const raw = row.getValue(columnId);
          const value = typeof raw === 'string' ? raw : undefined;
          return value ? filterValues.includes(value) : false;
        },
      },
    ],
    [selectedIds, bgNamesMap],
  );

  const sortedAssignments = useMemo(() => {
    const selectedSet = new Set(selectedIds);
    return [...scopedAssignments].sort((a, b) => {
      const aSelected = selectedSet.has(a.id);
      const bSelected = selectedSet.has(b.id);

      if (aSelected && !bSelected) {
        return -1;
      }

      if (!aSelected && bSelected) {
        return 1;
      }

      const aName = a.name || '';
      const bName = b.name || '';
      return aName.localeCompare(bName);
    });
  }, [scopedAssignments, selectedIds]);

  const table = useDataTable<Row>({
    data: sortedAssignments,
    columns,
    getRowId: r => r.id,
    externalState: {
      globalFilter: {
        state: globalSearch,
        setState: setGlobalSearch,
      },
    },
  });

  const handleClose = () => {
    setSelectedIds([]);
    setGlobalSearch('');
    onClose();
  };

  function assignSelected() {
    const initialSet = new Set(initialAssignmentIds);
    const selectedSet = new Set(selectedIds);

    const addAssignments = selectedIds.filter(id => !initialSet.has(id));
    const removeAssignments = initialAssignmentIds.filter(id => !selectedSet.has(id));

    if (addAssignments.length === 0 && removeAssignments.length === 0) {
      toast.info('No changes to save');
      handleClose();
      return;
    }

    bulkAssign.mutate(
      { userId, addAssignments, removeAssignments },
      {
        onSuccess() {
          const added = addAssignments.length;
          const removed = removeAssignments.length;
          const messages = [];

          if (added > 0) {
            messages.push(`${added} added`);
          }

          if (removed > 0) {
            messages.push(`${removed} removed`);
          }

          toast.success(`Assignments updated: ${messages.join(', ')}`);
          handleClose();
        },
        onError(error) {
          showErrorToast(error);
          handleClose();
        },
      },
    );
  }

  return (
    <ComposableSheet
      open={open}
      title='Assign Assignments'
      description='Assign user assignments.'
      side='right'
      widthClassName='w-full sm:max-w-5xl'
      deferClose
      onOpenChange={(o: boolean) => {
        if (!o) {
          handleClose();
        }
      }}
      footer={
        <div className='flex gap-2 w-full justify-between items-center'>
          <div className='text-xs text-muted-foreground'>
            {(() => {
              const initialSet = new Set(initialAssignmentIds);
              const selectedSet = new Set(selectedIds);
              const toAdd = selectedIds.filter(id => !initialSet.has(id)).length;
              const toRemove = initialAssignmentIds.filter(id => !selectedSet.has(id)).length;
              const parts = [];

              if (toAdd > 0) {
                parts.push(`+${toAdd}`);
              }

              if (toRemove > 0) {
                parts.push(`-${toRemove}`);
              }

              return parts.length > 0
                ? `Changes: ${parts.join(', ')}`
                : `Selected: ${selectedIds.length}`;
            })()}
          </div>
          <div className='flex gap-2'>
            <Button type='button' disabled={bulkAssign.isPending} onClick={assignSelected}>
              {bulkAssign.isPending ? 'Saving…' : 'Save'}
            </Button>
            <Button
              type='button'
              variant='outline'
              disabled={bulkAssign.isPending}
              onClick={onClose}
            >
              Cancel
            </Button>
          </div>
        </div>
      }
    >
      <div className='flex flex-col gap-4 p-4'>
        <div className='flex items-center gap-2'>
          <DebouncedInput
            type='text'
            value={globalSearch}
            onChange={v => {
              const next = v.toString();
              if (next === globalSearch) {
                return;
              }

              setGlobalSearch(next);
            }}
            placeholder='Search assignments…'
            className='h-9 rounded-md border bg-background px-3 text-sm shadow-sm flex-1'
          />
          {!businessGroupId && (
            <DataTableFacetedFilter
              column={table.getColumn('businessGroup')}
              title='Business Group'
              options={uniqueBusinessGroupIds.map(bgId => ({
                label: bgNamesMap.get(bgId) || bgId,
                value: bgId,
              }))}
            />
          )}
        </div>
        <div className='flex flex-col rounded-md border overflow-hidden'>
          {isLoading || isLoadingUserAssignments || isLoadingBusinessGroups ? (
            <DataTableSkeleton
              withPagination={false}
              rowCount={10}
              columnCount={columns.length}
              searchableColumnCount={0}
              filterableColumnCount={0}
              showViewOptions={false}
              cellWidths={['3rem', '14rem', '14rem']}
              shrinkZero
            />
          ) : isError ? (
            <div className='p-4 text-xs text-destructive'>
              Failed to load assignments: {error instanceof Error ? error.message : 'Unknown error'}
            </div>
          ) : scopedAssignments.length === 0 ? (
            <div className='p-4 text-xs text-muted-foreground'>No assignments found.</div>
          ) : (
            <DataTable
              table={table}
              className='h-[75vh]'
              emptyPlaceholderTitle='Assignments'
              emptyPlaceholderDescription='No assignments match filters.'
            />
          )}
        </div>
      </div>
    </ComposableSheet>
  );
}

export default AssignUserAssignmentsSheet;
